import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query';
import {
  DEFAULT_OCCASION_SETTINGS,
  type CustomOccasionInput,
  type Occasion,
  type OccasionDefinition,
  type OccasionSettings,
} from '@unipostudio/shared-types';
import { OCCASION_DATASET, localizedOccasionName } from '@unipostudio/platform-constants';
import { formatLocalDate, occasionsInRange, toLocalDate, upcomingOccasions } from '@/shared/lib/occasions/resolve';
import { occasionsService, type OccasionCatalogue, type OccasionCatalogueFilter } from '@/shared/services/occasions.service';
import { useWorkspaceStore } from '@/shared/stores/workspace.store';

/** Query key shared by every occasions read and mutation. */
export const OCCASIONS_KEY = 'occasions';

const UPCOMING_LIMIT = 6;
const UPCOMING_HORIZON_DAYS = 90;
const STALE_MS = 5 * 60 * 1000;

function useWsId(): string | undefined {
  return useWorkspaceStore((s) => s.currentWorkspace?.id);
}

/**
 * The reader's UI language, for occasion names: the curated dataset is
 * English data, shown in hi, bn, ml, ta, te, kn and ur where a name is listed
 * (platform-constants OCCASION_NAMES) and in English otherwise.
 */
function useNameLanguage(): string {
  const { i18n } = useTranslation();
  return i18n.language || 'en';
}

export function useOccasionSettings(): UseQueryResult<OccasionSettings> {
  const wsId = useWsId();
  return useQuery({
    queryKey: [OCCASIONS_KEY, 'settings', wsId],
    queryFn: () => occasionsService.getSettings(),
    enabled: !!wsId,
    staleTime: STALE_MS,
  });
}

export function useSaveOccasionSettings(): UseMutationResult<OccasionSettings, Error, Partial<OccasionSettings>> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<OccasionSettings>) => occasionsService.saveSettings(patch),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [OCCASIONS_KEY] });
    },
  });
}

export function useCustomOccasions(): UseQueryResult<OccasionDefinition[]> {
  const wsId = useWsId();
  return useQuery({
    queryKey: [OCCASIONS_KEY, 'custom', wsId],
    queryFn: () => occasionsService.listCustom(),
    enabled: !!wsId,
    staleTime: STALE_MS,
  });
}

export interface CustomOccasionMutations {
  add: UseMutationResult<OccasionDefinition, Error, CustomOccasionInput>;
  update: UseMutationResult<OccasionDefinition, Error, { id: string; patch: Partial<CustomOccasionInput> }>;
  remove: UseMutationResult<void, Error, string>;
}

export function useCustomOccasionMutations(): CustomOccasionMutations {
  const queryClient = useQueryClient();
  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: [OCCASIONS_KEY] });
  };
  const add = useMutation({
    mutationFn: (input: CustomOccasionInput) => occasionsService.addCustom(input),
    onSuccess: invalidate,
  });
  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<CustomOccasionInput> }) => occasionsService.updateCustom(id, patch),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: string) => occasionsService.removeCustom(id),
    onSuccess: invalidate,
  });
  return { add, update, remove };
}

export interface UseOccasionsResult {
  /** Every occasion touching the range, sorted by date then name. */
  occasions: Occasion[];
  /** Occasions keyed by their start day, YYYY-MM-DD. */
  byDay: Map<string, Occasion[]>;
  /** The next few occasions from today, for the calendar rail. */
  upcoming: Occasion[];
  settings: OccasionSettings;
  isLoading: boolean;
}

/**
 * The built-in dataset plus the workspace's custom occasions, resolved for a
 * date range under the workspace's settings (blueprint S20). Until settings
 * load, the defaults are used so the calendar never renders blank.
 */
export function useOccasions(range: { from: Date; to: Date }): UseOccasionsResult {
  const settingsQuery = useOccasionSettings();
  const customQuery = useCustomOccasions();
  const settings = settingsQuery.data ?? DEFAULT_OCCASION_SETTINGS;
  const custom = customQuery.data;
  // The public holidays the server imports for the workspace's country
  // (and its states). Curated occasions ship in the bundle; these do not.
  const importedQuery = useOccasionCatalogue({ source: 'imported', country: settings.country });
  const imported = importedQuery.data?.data;
  const fromKey = formatLocalDate(range.from);
  const toKey = formatLocalDate(range.to);
  const todayKey = formatLocalDate(new Date());
  const language = useNameLanguage();

  const { occasions, byDay, upcoming } = useMemo(() => {
    const defs: OccasionDefinition[] = [
      ...OCCASION_DATASET.map((d) => ({ ...d, name: localizedOccasionName(d.id, d.name, language), source: 'curated' as const })),
      ...(imported ?? []).map((d) => ({ ...d, source: 'imported' as const })),
      ...(custom ?? []).map((d) => ({ ...d, source: 'custom' as const })),
    ];
    const today = toLocalDate(todayKey);
    const inRange = occasionsInRange(defs, toLocalDate(fromKey), toLocalDate(toKey), settings, today);
    const grouped = new Map<string, Occasion[]>();
    for (const occasion of inRange) {
      const list = grouped.get(occasion.date);
      if (list) list.push(occasion);
      else grouped.set(occasion.date, [occasion]);
    }
    return {
      occasions: inRange,
      byDay: grouped,
      upcoming: upcomingOccasions(defs, settings, today, UPCOMING_LIMIT, UPCOMING_HORIZON_DAYS),
    };
  }, [custom, imported, fromKey, toKey, todayKey, settings, language]);

  return {
    occasions,
    byDay,
    upcoming,
    settings,
    isLoading: settingsQuery.isLoading || customQuery.isLoading,
  };
}

/**
 * The shared catalogue: curated occasions plus the public holidays the server
 * refreshes twice a month.
 *
 * Not workspace-scoped — it is the same catalogue for everyone — so it is
 * cached for an hour rather than the five minutes workspace data gets. The
 * data changes twice a month; re-fetching it every five minutes would be
 * three hundred requests for one change.
 */
export function useOccasionCatalogue(filter: OccasionCatalogueFilter = {}): UseQueryResult<OccasionCatalogue> {
  const language = useNameLanguage();
  // Curated names in the reader's language; imported holidays keep their own.
  const select = useCallback(
    (catalogue: OccasionCatalogue): OccasionCatalogue => ({
      ...catalogue,
      data: catalogue.data.map((d) => ({ ...d, name: localizedOccasionName(d.id, d.name, language) })),
    }),
    [language],
  );
  return useQuery({
    queryKey: [OCCASIONS_KEY, 'catalogue', filter],
    queryFn: () => occasionsService.listCatalogue(filter),
    staleTime: 60 * 60 * 1000,
    select,
  });
}
